import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import type { PageSection } from '@crowdex/plugin-contracts';
import { Button } from '@crowdex/ui/components/button';
import { EmptyState, InlineMessage } from '@crowdex/ui/components/feedback';
import { Input } from '@crowdex/ui/components/input';
import { toast } from '@crowdex/ui/components/toaster';
import { AppWindow, MessageSquareText, Plus, Search } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { ExpandableSection } from '@/components/library-page/expandable-section';
import { artifactGlyph, hasPreview, LibraryCard } from '@/components/library-page/library-card';
import {
  appNoun,
  libraryArtifacts,
  pluginArtifacts,
} from '@/components/library-page/library-model';
import { LIBRARY_GRID, PluginCards } from '@/components/library-page/library-plugin-section';
import { askAgent, attachToAgent } from '@/features/apps/agent-ask';
import {
  duplicateArtifact,
  showConversation,
  startAppConversation,
} from '@/features/apps/app-conversation';
import { useAppCreation } from '@/features/apps/app-creation';
import { openAppDialog } from '@/features/apps/app-dialog';
import { AppFrame } from '@/features/apps/app-frame';
import {
  appSections,
  fullScreenSectionsKey,
  refreshAppSidebar,
  setLiveSections,
  useAppSectionItems,
  useLiveSelection,
} from '@/features/apps/app-panel-sidebar';
import { artifactMatches } from '@/features/apps/artifact-grid';
import { ArtifactItemView } from '@/features/apps/artifact-item-view';
import { useAvatarOrigin, useItemOrigin } from '@/features/apps/artifact-origin';
import {
  type FrameAction,
  PluginFrame,
  useViewHeader,
  viewHeaderActions,
} from '@/features/apps/plugin-frame';
import { RemoteAppFullScreen } from '@/features/apps/remote-app-views';
import { rendersOf } from '@/features/apps/renders';
import { useArtifactIndex } from '@/features/apps/use-artifact-index';
import { inspectorView, useViewInspector, withInspector } from '@/features/apps/view-inspector';
import { pluginIcon } from '@/features/plugins/plugin-icons';
import {
  type PluginPanel,
  type PluginWorkbench,
  railPanels,
} from '@/features/plugins/plugin-workbench';
import {
  PluginWorkbenchContext,
  usePluginWorkbench,
} from '@/features/plugins/use-plugin-workbench';
import { goBackInApp, openAppHome } from '@/lib/app-home';
import { applyAppSettings, readAppSettings, resolveDesktopTheme } from '@/lib/app-settings';
import { useLibraryPage } from '@/lib/navigation';
import { pluginsPath } from '@/lib/plugins/plugin-sections';
import { navigateApp } from '@/vite/navigation-store';

/**
 * An app full screen, outside any conversation (docs/adr-plugin-model-uniformization.md R7–R8),
 * in the host's frame (`PluginFrame`), its sections in the host's left column: its home (one page
 * per app, the same layout for every app: example prompts and templates make starting easy, then
 * the user's items, then what else it provides; "New …" starts a conversation under the current
 * assistant with the app docked; a type's `create` makes it a guided creation, with a composer:
 * `useAppCreation`), a route of its view (`/apps/<plugin>/<route>`, its page view
 * full screen), an item (`?item=`, the app's page on it). With nothing selected the home shows,
 * never a page asking to pick one. A remote web app's home is its page.
 */
export function AppHomePage({
  plugin,
  item = null,
  route = null,
  deepLink = null,
  workbench: given,
  artifacts: givenArtifacts,
}: {
  plugin: string;
  /** The item open on the app's page. */
  item?: string | null;
  /** The route of its view (`/runs/42`). */
  route?: string | null;
  /** A remote app's deep link: the route with the address's query (`/mail?open=7`). */
  deepLink?: string | null;
  /** The apps, when the page does not read them itself (the preview). */
  workbench?: PluginWorkbench;
  /** The artifacts, when the page does not read the index itself (the preview). */
  artifacts?: readonly ArtifactListingEntry[];
}) {
  const loaded = usePluginWorkbench(null, given === undefined);
  const workbench = given ?? loaded;
  const read = useArtifactIndex(undefined, { enabled: givenArtifacts === undefined });
  const index =
    givenArtifacts === undefined
      ? read
      : { ...read, artifacts: givenArtifacts, loading: false, error: null };
  const library = useLibraryPage();
  const [theme, setTheme] = useState<'desktop-light' | 'desktop-dark'>('desktop-dark');
  useEffect(() => {
    const settings = readAppSettings();
    applyAppSettings(settings);
    setTheme(resolveDesktopTheme(settings.theme));
  }, []);
  const panel = railPanels(workbench).find(
    (candidate) =>
      candidate.plugin === plugin &&
      (candidate.appView !== undefined || candidate.remoteView !== undefined),
  );
  const sections = appSections(panel ?? null);
  const firstPart = route?.split('/').filter(Boolean)[0] ?? null;
  const section = sections.find((entry) => entry.id === firstPart) ?? null;
  // A route that is only a section, with nothing picked in it, is the home.
  const routeShown =
    route !== null &&
    panel?.appView !== undefined &&
    !(section !== null && route === `/${section.id}` && item === null);
  const opened =
    item === null || route !== null
      ? null
      : (index.artifacts.find((entry) => entry.id === item) ?? null);
  const icon = pluginIcon(panel?.icon ?? 'app');
  const close = () => library.leave();
  const back = () => {
    if (!goBackInApp()) openAppHome(plugin);
  };
  // An item opens full screen on its plugin's page, whether or not the plugin has a page of its own.
  const itemView =
    opened === null || opened.plugin !== plugin ? null : (
      <ArtifactItemView
        artifact={opened}
        onOpen={(target) => {
          const next = index.artifacts.find(
            (candidate) =>
              candidate.space === target.space &&
              candidate.path === target.path &&
              candidate.conversation === opened.conversation,
          );
          if (next !== undefined) openAppHome(next.plugin, next.id);
        }}
        placement="page"
      />
    );
  const frame = (props: {
    title: string;
    children: ReactNode;
    actions?: readonly FrameAction[];
    onBack?: () => void;
    layout?: 'full' | 'embedded';
  }) =>
    panel === undefined ? null : (
      <PluginFrame
        actions={props.actions}
        backLabel={panel.title}
        icon={icon}
        iconSrc={panel.iconSrc}
        layout={props.layout ?? 'full'}
        onBack={props.onBack}
        onClose={close}
        openElsewhere={{
          label: 'Open in a conversation',
          onSelect: () => void startAppConversation(panel.view),
        }}
        plugin={plugin}
        title={props.title}
      >
        {props.children}
      </PluginFrame>
    );
  const body =
    itemView !== null && panel?.remoteView === undefined ? (
      itemView
    ) : panel === undefined ? (
      workbench.loaded ? (
        <main className="flex h-full items-center justify-center">
          <EmptyState
            action={
              <Button onClick={() => navigateApp(pluginsPath('installed'))} variant="outline">
                Plugins
              </Button>
            }
            icon={<AppWindow />}
            title="This app is not available"
          />
        </main>
      ) : null
    ) : panel.remoteView !== undefined ? (
      // Its route and query are the deep link it opens (`/apps/crowdrive/files/<id>`,
      // `/apps/crowbox/mail?open=<id>`); its sections are in
      // the host's left column (`AppHomeColumn`), from the embed bridge.
      <RemoteAppFullScreen
        bridgeKey={fullScreenSectionsKey(plugin)}
        onClose={close}
        openElsewhere={{
          label: 'Open in a conversation',
          onSelect: () => void startAppConversation(panel.view),
        }}
        panel={panel}
        route={deepLink ?? route}
      />
    ) : routeShown && route !== null ? (
      <AppPageView item={item} panel={panel} route={route} section={section} />
    ) : item !== null && section === null ? (
      index.loading ? null : (
        frame({
          title: panel.title,
          onBack: back,
          children: (
            <main className="flex h-full items-center justify-center">
              <EmptyState
                action={
                  <Button onClick={() => openAppHome(plugin)} variant="outline">
                    Back to {panel.title}
                  </Button>
                }
                icon={<AppWindow />}
                title={`This ${appNoun(panel)} no longer exists`}
              />
            </main>
          ),
        })
      )
    ) : (
      <AppHome frame={frame} index={index} panel={panel} workbench={workbench} />
    );
  return (
    <PluginWorkbenchContext.Provider value={workbench}>
      <div
        // Inside the shell, which already keeps clear of a phone's notch and home indicator.
        className="desktop-shell flex h-full min-h-0 flex-col overflow-hidden bg-background p-0! text-foreground"
        data-app-home={plugin}
        data-theme={theme}
      >
        {body}
      </div>
    </PluginWorkbenchContext.Provider>
  );
}

/**
 * A level of an app's view full screen (`/apps/<plugin>/<route>`, an item of one of its
 * sections): the view in the host's frame, its bar filled by `page.header.set`, its levels
 * moved with `page.navigate` / `page.back`, its live sections in the host's left column.
 */
function AppPageView({
  panel,
  route,
  item,
  section,
}: {
  panel: PluginPanel;
  route: string;
  item: string | null;
  section: { id: string; title: string; route: string } | null;
}) {
  const plugin = panel.plugin;
  const appView = panel.appView;
  const viewHeader = useViewHeader();
  const inspector = useViewInspector();
  const origin = useAvatarOrigin(plugin, item);
  const library = useLibraryPage();
  const key = fullScreenSectionsKey(plugin);
  const selection = useLiveSelection(key);
  const listed = useAppSectionItems(panel, item === null ? null : section, { workspace: null });
  const picked = listed.items.find((entry) => entry.id === item);
  useEffect(() => () => setLiveSections(key, []), [key]);
  const setSections = useCallback((next: PageSection[]) => setLiveSections(key, next), [key]);
  if (appView === undefined) return null;
  const back = () => {
    if (!goBackInApp()) openAppHome(plugin);
  };
  const view = withInspector(inspector, {
    action: viewHeader.action,
    container: 'main',
    fragment: route,
    handlers: {
      ask: (text) => askAgent(text),
      attach: (selections, send) => attachToAgent(selections, send),
      open: (opened) => {
        if ('conversation' in opened) return showConversation(opened.conversation);
        if ('path' in opened && opened.in === 'dialog')
          return openAppDialog({
            plugin,
            path: opened.path,
            title: panel.title,
            input: opened.input,
            size: opened.size,
          });
        if ('path' in opened) {
          const url = new URL(opened.path, 'https://crowdex.invalid');
          const next = url.hash.startsWith('#/') ? url.hash.slice(1) : route;
          openAppHome(plugin, url.searchParams.get('item'), { route: next });
        }
        return null;
      },
      closeDialog: () => {
        throw new Error('This view is not a dialog.');
      },
      confirm: ({ title, message }) => window.confirm(message ? `${title}\n\n${message}` : title),
      selectItem: (next, nextSection) =>
        openAppHome(plugin, next, {
          route: nextSection === undefined ? route : `/${nextSection}`,
          replace: true,
        }),
      refreshPage: () => refreshAppSidebar(panel.view),
      setSections,
      setHeader: viewHeader.setHeader,
      navigate: (next, push) => openAppHome(plugin, null, { route: next, replace: !push }),
      back,
    },
    item,
    layout: 'full',
    media: appView.media,
    origin,
    path: appView.path,
    plugin: appView.plugin,
    route,
    section: section?.id ?? null,
    selection,
    title: panel.title,
  });
  return (
    <PluginFrame
      actions={viewHeaderActions(viewHeader.header, viewHeader.emit)}
      backLabel={panel.title}
      icon={pluginIcon(panel.icon ?? 'app')}
      iconSrc={panel.iconSrc}
      inspector={inspectorView(inspector, view)}
      layout="full"
      onBack={back}
      onClose={() => library.leave()}
      openElsewhere={{
        label: 'Open in a conversation',
        onSelect: () => void startAppConversation(panel.view),
      }}
      plugin={plugin}
      status={viewHeader.header?.status}
      title={viewHeader.header?.title ?? picked?.title ?? section?.title ?? panel.title}
    >
      <AppFrame {...view} />
    </PluginFrame>
  );
}

function AppHome({
  panel,
  workbench,
  index,
  frame,
}: {
  panel: PluginPanel;
  workbench: PluginWorkbench;
  index: {
    artifacts: readonly ArtifactListingEntry[];
    loading: boolean;
    error: string | null;
    refresh(): void;
  };
  frame(props: { title: string; children: ReactNode; actions?: readonly FrameAction[] }): ReactNode;
}) {
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const originOf = useItemOrigin();
  const noun = appNoun(panel);
  const all = useMemo(() => libraryArtifacts(index.artifacts), [index.artifacts]);
  const yours = useMemo(
    () => all.filter((artifact) => artifact.plugin === panel.plugin),
    [all, panel.plugin],
  );
  const provided = useMemo(
    () => pluginArtifacts(index.artifacts).filter((artifact) => artifact.plugin === panel.plugin),
    [index.artifacts, panel.plugin],
  );
  const matching = (artifacts: readonly ArtifactListingEntry[]) =>
    artifacts.filter((artifact) => artifactMatches(artifact, query));
  const templates = matching(provided.filter((artifact) => artifact.template));
  const others = matching(provided.filter((artifact) => !artifact.template));
  const shown = matching(yours);
  const examples = (panel.examples ?? []).filter(
    (example) =>
      query.trim() === '' ||
      `${example.title} ${example.prompt}`.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const searching = query.trim() !== '';
  const failed = (title: string) => (error: unknown) => {
    setBusy(null);
    toast.error(title, error instanceof Error ? error.message : String(error));
  };
  // A new, plain conversation, the app docked (docs/adr-assistants.md).
  const start = (key: string, options: { prompt?: string } = {}) => {
    setBusy(key);
    void startAppConversation(panel.view, options).then(() => setBusy(null), failed(`New ${noun}`));
  };
  // A template's "Use" is its Duplicate: the copy opens in a new conversation, composer focused.
  const use = (artifact: ArtifactListingEntry) => {
    setBusy(artifact.id);
    void duplicateArtifact(artifact, 'new').then(
      () => setBusy(null),
      failed(`Use ${artifact.title}`),
    );
  };
  // A type with a guided creation (`types[].create`): "New …" runs its skill, the composer too.
  const creation = useAppCreation(panel);
  const startNew = () => (creation === null ? start('new') : creation.start());
  const newLabel = creation?.label ?? `New ${noun}`;
  const open = (artifact: ArtifactListingEntry) => openAppHome(artifact.plugin, artifact.id);
  const memory = (section: string) => `${panel.plugin}/${section}`;
  return frame({
    title: panel.title,
    actions: [
      {
        id: 'new',
        label: newLabel,
        icon: Plus,
        primary: true,
        disabled: busy !== null || creation?.starting === true,
        onSelect: startNew,
      },
    ],
    children: (
      <div className="flex h-full min-h-0 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto px-8 pt-6 pb-8 max-md:px-4 max-md:pb-24">
          <div className="relative mb-6 w-72 max-md:w-full">
            <Search
              aria-hidden
              className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-3.5 size-4 text-muted-foreground"
            />
            <Input
              aria-label={`Search ${panel.title}`}
              className="h-control-md rounded-control border-transparent bg-control pl-10 shadow-none"
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`Search ${panel.title}`}
              value={query}
            />
          </div>
          <div className="flex flex-col gap-8">
            {examples.length > 0 && (
              <ExpandableSection
                batch="row"
                items={examples}
                memoryKey={memory('examples')}
                title="Ask for"
              >
                {(visible) => (
                  <ul aria-label={`${panel.title} examples`} className={LIBRARY_GRID}>
                    {visible.map((example) => (
                      <li key={example.title}>
                        <button
                          className="flex h-full w-full flex-col gap-1 rounded-2xl bg-card px-3.5 py-3 text-left transition-colors hover:bg-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                          data-app-example
                          disabled={busy !== null}
                          onClick={() =>
                            creation === null
                              ? start(`example:${example.title}`, { prompt: example.prompt })
                              : creation.prefill(example.prompt)
                          }
                          type="button"
                        >
                          <span className="flex min-w-0 items-center gap-2 font-medium text-sm">
                            <MessageSquareText
                              aria-hidden
                              className="size-4 shrink-0 text-muted-foreground"
                            />
                            <span className="truncate">{example.title}</span>
                          </span>
                          <span
                            className="type-meta line-clamp-2 text-muted-foreground"
                            title={example.prompt}
                          >
                            {example.prompt}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </ExpandableSection>
            )}
            {templates.length > 0 && (
              <ExpandableSection
                batch="row"
                items={templates}
                memoryKey={memory('templates')}
                title="Start from a template"
              >
                {(visible) => (
                  <PluginCards
                    artifacts={visible}
                    label={`${panel.title} templates`}
                    onOpen={open}
                    onUse={use}
                    using={busy}
                    workbench={workbench}
                  />
                )}
              </ExpandableSection>
            )}
            <ExpandableSection
              batch="row"
              items={shown}
              memoryKey={memory('yours')}
              title={`Your ${noun}s`}
            >
              {(visible) =>
                index.error !== null && index.artifacts.length === 0 ? (
                  <InlineMessage
                    actions={
                      <Button onClick={index.refresh} size="sm" type="button" variant="outline">
                        Retry
                      </Button>
                    }
                    title={`Couldn't load your ${noun}s.`}
                    tone="error"
                  >
                    {index.error}
                  </InlineMessage>
                ) : index.loading && index.artifacts.length === 0 ? null : shown.length === 0 ? (
                  <EmptyState
                    action={
                      searching ? (
                        <Button onClick={() => setQuery('')} size="sm" variant="outline">
                          Clear
                        </Button>
                      ) : (
                        <Button
                          disabled={busy !== null}
                          onClick={startNew}
                          size="sm"
                          variant="outline"
                        >
                          {newLabel}
                        </Button>
                      )
                    }
                    title={searching ? `No ${noun} matches “${query.trim()}”` : `No ${noun}s`}
                  />
                ) : (
                  <ul aria-label={`Your ${noun}s`} className={LIBRARY_GRID}>
                    {visible.map((artifact) => {
                      const origin = originOf(artifact.conversation);
                      return (
                        <LibraryCard
                          artifact={artifact}
                          glyph={artifactGlyph(artifact, workbench)}
                          key={artifact.id}
                          onOpen={open}
                          origin={
                            origin === null
                              ? null
                              : { name: origin.assistant.name, color: origin.assistant.color }
                          }
                          preview={hasPreview(artifact, workbench)}
                          renders={rendersOf(artifact, all)}
                        />
                      );
                    })}
                  </ul>
                )
              }
            </ExpandableSection>
            {others.length > 0 && (
              <ExpandableSection
                batch="row"
                items={others}
                memoryKey={memory('provided')}
                title={`From ${panel.title}`}
              >
                {(visible) => (
                  <PluginCards
                    artifacts={visible}
                    label={`From ${panel.title}`}
                    onPick={open}
                    using={busy}
                    workbench={workbench}
                  />
                )}
              </ExpandableSection>
            )}
          </div>
        </div>
        {creation === null ? (
          <Button
            className="fixed right-4 bottom-[calc(env(safe-area-inset-bottom)+1rem)] z-10 h-12 rounded-control px-5 shadow-lg md:hidden"
            disabled={busy !== null}
            onClick={() => start('new')}
            type="button"
          >
            <Plus className="size-4" />
            New {noun}
          </Button>
        ) : (
          creation.composer
        )}
      </div>
    ),
  });
}
